import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import ViewAsset from "./ViewAsset";
import AddAsset from "./AddAsset";
import SnackBar from "../SnackBar.js";
import ApiSession from "../../api/index.js";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { Box, Button, useMantineColorScheme } from "@mantine/core";
import { useAtom } from "jotai";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";

import "./index.scss";
import { SeIcon } from "@se/web-ui-react";
import { getErrorMessage } from "../../utils/errorUtils.js";

const apiSession = new ApiSession();

const LibraryManagementHook = () => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const isLightMode = colorScheme === "light";
  const backgroundColor = isLightMode ? "#FFFFFF" : "var(--se-background-alternative)";
  const textCellSx = {
    fontWeight: "normal",
    fontSize: "14px",
    fontFamily: "Nunito",
    backgroundColor,
  };
  const [loading, setLoading] = useState(true);
  const [enableAddEdit, setEnableAddEdit] = useState(false);
  const [addStatus, setAddStatus] = useState(false);
  const [projectTypes] = useState([]);
  const [asset, setAsset] = useState({
    type: "",
    value: "",
  });
  const [defaultAssetAttribute, setDefaultAssetAttribute] = useState([]);
  const [defaultAvailableAttributeName, setDefaultAvailableAttributeName] =
    useState([]);
  const [currentAvailableAttributes, setCurrentAvailableAttributes] = useState(
    []
  );
  const [assetDetails, setAssetDetails] = useState({
    assets: [],
    assetsData: [],
    assetsType: [],
    assetAttributes: [],
  });
  const [userScope] = useAtom(userScopeAtom);
  const canSaveAssets = !!(userScope && userScope.access && userScope.access.saveAssets);
  const [isExtendedLibrary, setIsExtendedLibrary] = useState(false);
  const [assetInfo, setAssetInfo] = useState({
    allManufacturerList: [],
    manufacturerList: {},
  });
  const [viewAssets, setViewAssets] = useState({});
  const [updatedAsset, setUpdatedAsset] = useState({});
  const [deletedAsset, setDeletedAsset] = useState({});
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });

  const [popupStatus, setPopupStatus] = useState({
    addAssetPopupStatus: false,
    viewAssetPopupStatus: false,
    deleteConfirmPopUpStatus: false,
  });
  const [renderFunction, setRenderFunction] = useState({
    renderAssetDetails: false,
  });
  const [editAssetData, setEditAssetData] = useState({});
  const snackBarRef = useRef(null);
  const inputRefs = useRef([]);

  const focusInput = (index) => {
    if (inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  };

  useEffect(() => {
    listAssets();
    getDefaultAssetsAttributeList();
  }, []);

  // Add tour attributes to table headers similar to DataTable/UserTable
  useEffect(() => {
    const theads = document.querySelectorAll('.mantine-Table-root thead');
    theads.forEach(thead => {
      if (!thead.getAttribute('data-intro')) {
        thead.setAttribute('data-intro', t('library.tableHeaderIntro'));
        thead.setAttribute('data-position', 'bottom');
      }
    });
  }, []);

  useEffect(() => {
    let allManufacturerListNew = [];
    let manufacturerListAll = {};
    if (renderFunction.renderAssetDetails) {
      assetDetails.assetsType.forEach(
        (assetTypeData) => (manufacturerListAll[assetTypeData.type] = [])
      );

      assetDetails.assets.forEach((asset) => {
        if (manufacturerListAll[asset.type.type]) {
          manufacturerListAll[asset.type.type].push(asset.manufacturer);
          manufacturerListAll[asset.type.type].sort((a, b) => a.localeCompare(b));
        }

        if (!allManufacturerListNew.includes(asset.manufacturer)) {
          allManufacturerListNew = [
            ...allManufacturerListNew,
            asset.manufacturer,
          ];
        }
      });

      setAssetInfo((prevState) => ({
        ...prevState,
        allManufacturerList: allManufacturerListNew.sort((a, b) => a.localeCompare(b)),
        manufacturerList: manufacturerListAll,
      }));
      setEnableAddEdit(true);
    }
  }, [renderFunction]);

  const getDefaultAssetsAttributeList = async (value = "Generator") => {
    try {
      const response = await apiSession.getDefaultsAssetsAttributes(value);
      setDefaultAssetAttribute(response?.defaultAttributeList);
      setDefaultAvailableAttributeName((prev) => {
        return response?.allowedAttributeName;
      });

      setCurrentAvailableAttributes(response?.allowedAttributeName);
      return response;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };
  /* istanbul ignore next */
  const listAssets = () => {
    apiSession
      .getAssets()
      .then((response) => {
        setAssetDetails((prevState) => ({
          ...prevState,
          assets: response,
          assetsData: response,
        }));

        listAssetType();
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const listAssetType = () => {
    apiSession
      .getAssetType()
      .then((response) => {
        setAssetDetails((prevState) => ({
            ...prevState,
            assetsType: response,
          }));

          const renderFunctionsNew = { renderAssetDetails: true };
          setRenderFunction(renderFunctionsNew);
        })
        .catch(() => {});
  };

  const resetAssetList = () => {
    setAssetDetails((prevState) => ({
      ...prevState,
      assets: prevState.assetsData,
    }));
  };

  const addAsset = () => {
    setAddStatus(true);
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: true,
    }));
  };

  const getAssetDetailsView = (assetId) => {
    setLoading(true);
    apiSession
      .getAssetById(assetId)
      .then((response) => {
        setViewAssets(response);
        setPopupStatus((prevState) => ({
          ...prevState,
          viewAssetPopupStatus: true,
        }));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        callSnackbar("error",getErrorMessage(error));
      });
  };

  const closeViewPopup = () =>
    setPopupStatus((prevState) => ({
      ...prevState,
      viewAssetPopupStatus: false,
    }));

  const editAsset = (assetId) => {
    setLoading(true);
    apiSession
      .getAssetById(assetId)
      .then((response) => {
        getDefaultAssetsAttributeList(response?.type?.type)
        setUpdatedAsset(response);
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: response?.attributes,
        }));
        setIsExtendedLibrary(response?.isExtendedLibrary);
        setAddStatus(false);
        setPopupStatus((prevState) => ({
          ...prevState,
          addAssetPopupStatus: true,
        }));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        callSnackbar("error", getErrorMessage(error));
      });
  };

  const onCloseAssetPopUp = () => {
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: false,
    }));
    setAddStatus(false);
  };

  const onUpdateAsset = (data) => {
    setLoading(true);
    data.attributes = [];
    let newAttribues = [];
    newAttribues = [...assetDetails.assetAttributes].map(
      ({ isEdit, ...rest }) => rest
    );
    newAttribues.forEach((attribData, index) => {
      newAttribues[index].id = 0;
      newAttribues[index].assetId = 0;
    });
    data.isExtendedLibrary = isExtendedLibrary;
    data.status = "A";
    data.assetName = "";
    data.attributes = newAttribues;
    saveAsset(data);
  };

  const saveAsset = (jsonBody) => {
    apiSession
      .saveAssets(jsonBody)
      .then((response) => {
        // Refresh the full asset list from backend to ensure table updates
        listAssets();
        // Keep local optimistic update as fallback (no harm if listAssets overwrites)
        addUpdateDataToAssetList(response, addStatus);
        if (addStatus === true) {
          callSnackbar("success", t('library.asset.addSuccess'));
        } else {
          callSnackbar("success", t('library.asset.updateSuccess'));
        }
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: [],
        }));
        setAddStatus(false);
        setLoading(false);
        setPopupStatus((prevState) => ({
          ...prevState,
          addAssetPopupStatus: false,
        }));
      })
      .catch((error) => {
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: [],
        }));
        setAddStatus(false);
        setLoading(false);
        setPopupStatus((prevState) => ({
          ...prevState,
          addAssetPopupStatus: false,
        }));
        callSnackbar("error", getErrorMessage(error));
      });
  };

  const addUpdateDataToAssetList = (data, status) => {
    if (status) {
      let newAssetList = [...assetDetails.assetsData];
      newAssetList.push(data);
      setAssetDetails((prevState) => ({
        ...prevState,
        assets: newAssetList,
        assetsData: newAssetList,
      }));
    } else {
      let assetIndex = 0;
      let newAssetList = [...assetDetails.assetsData];
      newAssetList.forEach((assetData, index) => {
        if (assetData.assetId === data.assetId) {
          assetIndex = index;
        }
      });
      if (assetIndex) {
        newAssetList[assetIndex] = data;
      }
      setAssetDetails((prevState) => ({
        ...prevState,
        assets: newAssetList,
        assetsData: newAssetList,
      }));
    }
  };

  const getAssetDetailsAttribute = (assetType) => {
    const assetId = Number(assetType);
    const assetData = [...defaultAssetAttribute];
    const selectedAssetData = assetData
      ?.filter((data) => data.id === assetId)?.[0]
      ?.defaultAttributes?.map((attr, index) => ({
        type: attr.attributeName,
        value: attr.attributeValue,
        id: index,
        isEdit: false,
      }));
    const typesToRemove = selectedAssetData?.map((item) => item.type);
    const filteredSecondArray = defaultAvailableAttributeName?.filter(
      (item) => !typesToRemove?.includes(item)
    );
    setCurrentAvailableAttributes(filteredSecondArray);
    if (addStatus) {
      setAssetDetails((prevState) => ({
        ...prevState,
        assetAttributes: selectedAssetData || [],
      }));
    }

    let attribuetsNew = assetDetails.assetsData.filter(
      (assetsData) => assetsData.type.id === parseInt(assetType, 10)
    );

    let indexAssetId = 0;
    if (parseInt(assetType, 10) === 2) {
      indexAssetId = 1;
    } else if (parseInt(assetType, 10) === 3) {
      attribuetsNew = assetDetails.assets.filter(
        (assetsData) => assetsData.manufacturer === "STANDARD BESS TEMPLATE"
      );
      indexAssetId = 0;
    }
  };
  const handleAssetAttribute = (e, assetAttribute) => {
    const { name, value } = e.target;
    if (/^(\d+(\.\d+)?|)$/.test(value)) {
      setAssetDetails((prevState) => {
        const updateAsset = prevState.assetAttributes.map((asset) => {
          if (asset.id === assetAttribute.id) {
            return { ...asset, [name]: value };
          }
          return { ...asset };
        });
        setUpdatedAsset({ attributes: updateAsset });
        return {
          ...prevState,
          assetAttributes: updateAsset,
        };
      });
    }
  };
  const handleAsset = (e) => {
    const { name, value } = e.target;
    const checkValue = value === "" || /^\d+(\.\d+)?$/.test(value);
    if (checkValue) {
      setAsset((prev) => {
        return { ...prev, [name]: value };
      });
    }
  };
  const handleAssetSelection = (e) => {
    const { name, value } = e.target;
    setAsset((prev) => {
      return { ...prev, [name]: value };
    });
  };
  const addAttributeToLibrary = (index = 0) => {
    let newAttributeData = { id: index, ...asset };
    const uniqueName = assetDetails?.assetAttributes
      .map((item) => item.type)
      .includes(newAttributeData.type);
    const filteredSecondArray = currentAvailableAttributes?.filter(
      (item) => item !== newAttributeData.type
    );
    setCurrentAvailableAttributes(filteredSecondArray);
    if (newAttributeData.type !== "" && newAttributeData.value !== "") {
      if (!uniqueName) {
        setAssetDetails((prevState) => {
          if (prevState?.assetAttributes) {
            return {
              ...prevState,
              assetAttributes: [
                ...prevState.assetAttributes,
                newAttributeData,
              ],
            };
          } else {
            return {
              ...prevState,
              assetAttributes: [newAttributeData],
            };
          }
        });
        setAsset({
          type: "",
          value: "",
        });
      } else {
        callSnackbar("error", t('library.asset.uniqueType'));
      }
      } else {
        callSnackbar("error", t('library.error.attributesMandatory'));
      }
  };
  const handleEditAsset = (data) => {
    setEditAssetData(data);
    setAssetDetails((prevState) => {
      const enableAsset = prevState.assetAttributes.map((asset) => {
        if (asset.id === data.id) {
          focusInput(asset.id);
          return { ...asset, isEdit: true };
        } else {
          return { ...asset, isEdit: false };
        }
      });
      return {
        ...prevState,
        assetAttributes: enableAsset,
      };
    });
  };
  const handleCancelUpdateAsset = (assetData) => {
    setAssetDetails((prevState) => {
      const updatedAsset = prevState.assetAttributes.map((asset) => {
        if (asset.id === assetData.id) {
          return { ...editAssetData, isEdit: false };
        }
        return { ...asset, isEdit: false };
      });

      return {
        ...prevState,
        assetAttributes: updatedAsset,
      };
    });
  };
  const handleOkEditAsset = () => {
    setAssetDetails((prevState) => {
      const enableAsset = prevState.assetAttributes.map((asset) => {
        if (asset.value === "") {
          callSnackbar("error", t('library.error.attributesMandatory'));
          return { ...asset };
        } else {
          return { ...asset, isEdit: false };
        }
      });

      return {
        ...prevState,
        assetAttributes: enableAsset,
      };
    });
  };
  const handleDeleteAsset = (id) => {
    setAssetDetails((prevState) => {
      const updateAsset = prevState.assetAttributes.filter(
        (asset) => asset.id !== id
      );
      return {
        ...prevState,
        assetAttributes: updateAsset,
      };
    });
  };
  const deleteAsset = (assetId) => {
    setLoading(true);
    apiSession
      .getAssetById(assetId)
      .then((response) => {
        setPopupStatus((prevState) => ({
          ...prevState,
          deleteConfirmPopUpStatus: true,
        }));
        setDeletedAsset(response);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        callSnackbar("error", getErrorMessage(error));
      });
  };

  const removeAssetConfirm = () => removeAssets(deletedAsset);

  const removeAssets = (assetData) => {
    assetData.status = "D";
    assetData.assetName = "";
      apiSession
        .saveAssets(assetData)
        .then(() => {
          // Re-fetch assets to reflect deletion in table
          listAssets();
          callSnackbar("success", t('library.asset.deleteSuccess'));
          removeFromAssetList(assetData.assetId);
          closeDeletePopUp();
        })
        .catch((error) => {
          closeDeletePopUp();
          callSnackbar("error", getErrorMessage(error));
        });
  };

  const removeFromAssetList = (id) => {
    let newAssetList = Object.assign([], assetDetails.assetsData);
    let newAssetListDeleted = newAssetList.filter(
      (dataElement) => dataElement.assetId !== id
    );
    setAssetDetails((prevState) => ({
      ...prevState,
      assets: newAssetListDeleted,
      assetsData: newAssetListDeleted,
    }));
    resetAssetList();
  };

  const cancelAssetConfirm = () => closeDeletePopUp();

  const closeDeletePopUp = () => {
    setPopupStatus((prevState) => ({
      ...prevState,
      deleteConfirmPopUpStatus: false,
    }));
    setDeletedAsset({});
  };

  const callSnackbar = useCallback((type, message) => {
    try {
      setSnackBar({ type, message });
      snackBarRef.current.open = true;
      if( type === "error" ){
        snackBarRef.current.canClose = true;
      }
    } catch (error) {
      console.log("error in snack bar");
    }
  }, []);

  const data = useMemo(
    () =>
      assetDetails?.assets.map((assetData) => ({
        manufacturer: assetData?.manufacturer,
        asset: assetData?.type?.type,
        category: assetData?.assetInfo,
        model: assetData?.communicationModel,
        version: assetData?.version,
        creationDate: assetData?.creationDate,
        status: assetData?.validated ? t('profile.status.active') : t('profile.status.inactive'),
        action: assetData?.assetId,
      })),
    [assetDetails?.assets, t]
  );

  const columns = [
    { accessorKey: "manufacturer", header: t('library.table.manufacturer'), size: 150 },
    { accessorKey: "asset", header: t('library.table.assetType'), size: 60 },
    { accessorKey: "category", header: t('library.table.category'), size: 80 },

    { accessorKey: "model", header: t('library.table.model'), size: 100 },
    { accessorKey: "version", header: t('library.table.version'), size: 50 },
    { accessorKey: "creationDate", header: t('library.table.date'), size: 100 },
    { accessorKey: "status", header: t('library.table.status'), size: 50 },
    {
      accessorKey: "action",
      header: t('library.table.action'),
      size: 100,
      Cell: ({ cell }) => (
        <>
          <span className="pointer">
            <se-icon
              size="small"
              color="primary"
              title={t('library.viewMore')}
              onClick={() => getAssetDetailsView(cell.getValue())}
              data-testid="view-asset"
              {...(cell?.row?.index === 0 ? { 'data-intro': t('library.clickViewAssetAttribute'), 'data-position': 'bottom' } : {})}

            >
              body_eye
            </se-icon>
          </span>

          <span>&nbsp; &nbsp;</span>
          { !canSaveAssets ? null : 
          <span className="pointer">
            <se-icon
              size="small"
              title={t('library.edit')}
              color="primary"
              onClick={() => editAsset(cell.getValue())}
              {...(cell?.row?.index === 0 ? { 'data-intro': t('library.editAssetAttributes'), 'data-position': 'bottom' } : {})}
            >
              action_editor
            </se-icon>
          </span>}
          <span>&nbsp; &nbsp;</span>
          {!canSaveAssets ? null :
           <span className="pointer">
            <se-icon
              size="small"
              title={t('library.delete')}
              color="primary"
              disabled={!canSaveAssets}
              onClick={() => deleteAsset(cell.getValue())}
              {...(cell?.row?.index === 0 ? { 'data-intro': t('library.deleteAssetFromLibrary'), 'data-position': 'bottom-left' } : {})}
            >
              action_delete
            </se-icon>
          </span>}
        </>
      ),
    },
  ];

  const table = useMantineReactTable({
    columns,
    data,
    enableFullScreenToggle: true,
    enableStickyHeader: true,
    positionGlobalFilter: "right",
    initialState: {
      density: "xs",
      showGlobalFilter: true,
    },
    mantineTableContainerProps: {
      sx: { height: assetDetails?.assets?.length > 9 ? "auto" : "500px" },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: { backgroundColor },
    },
    mantineTableBodyProps: {
      sx: { ...textCellSx, maxHeight: "1000px" },
    },
    mantineSearchTextInputProps: {
      placeholder: t('library.searchAllAssets'),
      sx: { minWidth: "400px" },
      "data-intro": t('library.searchIntro'),
      "data-position": "bottom",
    },
    mantineTopToolbarProps: { sx: { backgroundColor } },
    mantineBottomToolbarProps: { sx: { padding: "10px", backgroundColor } },
    mantineTableBodyCellProps: { sx: textCellSx },
    mantineTableHeadCellProps: {
      sx: { ...textCellSx, borderTop: "1px solid #dee2e6 !important" },
    },
    globalFilterModeOptions: ["fuzzy"],
    renderTopToolbarCustomActions: ({ table }) => (
        <Box
        className="box-header"
        data-intro={t('library.addAssetIntro')}
        data-position="bottom"
      >
        <Button
          option="raised"
          color="seGreen.0"
          leftIcon={<SeIcon>action_add_flat</SeIcon>}
          onClick={addAsset}
          style={{
            color: !isLightMode ? "#2e3032" : "#ffffff",
          }}
          disabled={!canSaveAssets}
        >
          {t('library.addAsset')}
        </Button>
      </Box>
    ),
  });

  return (
    <div className="container-fluid">
      <se-loading loading={loading}></se-loading>
      <div className="row">
        <div className="col-sm-1"></div>
        <div className="col-sm-11 mt-3">
          <div className="col-sm-11" style={{ height: "100%" }}>
            <MantineReactTable table={table} className="maintain-zoom" />
          </div>
          <SnackBar snackBar={snackBar} ref={snackBarRef} />
          {popupStatus.viewAssetPopupStatus && (
            <ViewAsset
              viewAssets={viewAssets}
              viewAssetPopupStatus={popupStatus.viewAssetPopupStatus}
              closeViewPopup={closeViewPopup}
            />
          )}

          {popupStatus.addAssetPopupStatus && (
            <AddAsset
              getDefaultAssetsAttributeList={getDefaultAssetsAttributeList}
              addAssetPopupStatus={popupStatus.addAssetPopupStatus}
              addStatus={addStatus}
              assetsType={assetDetails?.assetsType}
              assetAttributes={assetDetails?.assetAttributes}
              assetDetailsData={assetDetails}
              manufacturerList={assetInfo.manufacturerList}
              updatedAsset={updatedAsset}
              onCloseAssetPopUp={onCloseAssetPopUp}
              onUpdateAsset={onUpdateAsset}
              getAssetDetailsAttribute={getAssetDetailsAttribute}
              addAttributeToLibrary={addAttributeToLibrary}
              handleAsset={handleAsset}
              handleAssetSelection={handleAssetSelection}
              asset={asset}
              projectTypes={projectTypes}
              handleEditAsset={handleEditAsset}
              handleDeleteAsset={handleDeleteAsset}
              handleAssetAttribute={handleAssetAttribute}
              handleOkEditAsset={handleOkEditAsset}
              handleCancelUpdateAsset={handleCancelUpdateAsset}
              setIsExtendedLibrary={setIsExtendedLibrary}
              isExtendedLibrary={isExtendedLibrary}
              inputRefs={inputRefs}
              currentAvailableAttributes={currentAvailableAttributes}
              defaultAssetAttribute={defaultAssetAttribute}
              setAssetDetailsEx={setAssetDetails}
              setCurrentAvailableAttributes={setCurrentAvailableAttributes}
              isAssetTypeDisabled={false}
            />
          )}

          <se-dialog
            id="dialog-complex-delete-confirm"
            open={popupStatus.deleteConfirmPopUpStatus}
            can-backdrop="false"
          >
            <se-dialog-header>{t('common.confirmation')}</se-dialog-header>
            <se-dialog-content>
              <span className="se-font-14px">
                {t('library.confirmDelete')}
              </span>
            </se-dialog-content>
            <se-dialog-footer>
              <se-button aria-label="library-delete-confirm-cancel" onClick={cancelAssetConfirm}>{t('common.cancel')}</se-button>
              <se-button aria-label="library-delete-confirm-ok" onClick={removeAssetConfirm} option="flat" color="primary">{t('profile.ok')}</se-button>
            </se-dialog-footer>
          </se-dialog>
        </div>
      </div>
    </div>
  );
};

export default LibraryManagementHook;
